import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Typography,
  Toolbar,
  InputBase,
  IconButton,
  Chip,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { EmptyState } from '../Common';

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(1.5, 2),
  '&.MuiTableCell-head': {
    backgroundColor: theme.palette.background.default,
    fontWeight: 600
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.background.default
  },
  '&:last-child td, &:last-child th': {
    border: 0
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    cursor: 'pointer'
  }
}));

const StyledSearchBar = styled('div')(({ theme }) => ({
  position: 'relative',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 3,
  backgroundColor: theme.palette.background.paper,
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto'
  }
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch'
    }
  }
}));

/**
 * Styled data table component with consistent styling, sorting, and pagination
 * 
 * @param {Object} props - Component props
 * @param {Array} props.columns - Array of column definitions
 * @param {Array} props.data - Data array
 * @param {String} props.title - Table title
 * @param {String} props.defaultSortColumn - Default column to sort by
 * @param {Function} props.onRowClick - Function called when a row is clicked
 * @param {Boolean} props.loading - Whether the table is loading
 * @param {Function} props.onRefresh - Function called when refresh button is clicked
 * @param {Boolean} props.hasSearch - Whether to show search bar
 * @param {Function} props.onSearch - Function called when search is performed
 * @param {Boolean} props.hasFilters - Whether to show filter button
 * @param {Function} props.onFilter - Function called when filter button is clicked
 * @param {Boolean} props.hasExport - Whether to show export button
 * @param {Function} props.onExport - Function called when export button is clicked
 * @param {Object} props.sx - Additional styling for the table container
 */
const StyledDataTable = ({
  columns = [],
  data = [],
  title,
  defaultSortColumn,
  onRowClick,
  loading = false,
  onRefresh,
  hasSearch = false,
  onSearch,
  hasFilters = false,
  onFilter,
  hasExport = false,
  onExport,
  sx = {}
}) => {
  // State for sorting
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(defaultSortColumn || '');
  
  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // State for search
  const [searchTerm, setSearchTerm] = useState('');
  
  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle search
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };
  
  // Sort function
  const sortData = (array, comparator) => {
    if (!orderBy) return array;
    
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };
  
  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };
  
  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };
  
  // Apply pagination
  const paginatedData = sortData(data, getComparator(order, orderBy))
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        width: '100%', 
        overflow: 'hidden',
        borderRadius: 3,
        border: '1px solid rgba(0, 0, 0, 0.05)',
        ...sx
      }}
    >
      {/* Toolbar with title, search and actions */}
      {(title || hasSearch || onRefresh || hasFilters || hasExport) && (
        <Toolbar
          sx={{
            p: 2,
            pl: { sm: 3 },
            pr: { xs: 1, sm: 1 },
            justifyContent: 'space-between'
          }}
        >
          {/* Title */}
          {title && (
            <Typography
              variant="h6"
              sx={{ flex: '1 1 100%', fontWeight: 600 }}
            >
              {title}
            </Typography>
          )}
          
          {/* Search and actions */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {hasSearch && (
              <StyledSearchBar>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <StyledInputBase
                  placeholder="Search…"
                  inputProps={{ 'aria-label': 'search' }}
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </StyledSearchBar>
            )}
            
            {hasFilters && (
              <Tooltip title="Filter list">
                <IconButton onClick={onFilter}>
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            )}
            
            {onRefresh && (
              <Tooltip title="Refresh">
                <IconButton onClick={onRefresh}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            )}
            
            {hasExport && (
              <Tooltip title="Export">
                <IconButton onClick={onExport}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      )}
      
      {/* Loading indicator */}
      {loading && (
        <LinearProgress />
      )}
      
      {/* Table or empty state */}
      {data.length === 0 && !loading ? (
        <EmptyState
          title="No data available"
          description="There are no records to display"
          actionLabel={onRefresh ? "Refresh" : null}
          onAction={onRefresh}
        />
      ) : (
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ minWidth: column.minWidth, width: column.width }}
                    sortDirection={orderBy === column.id ? order : false}
                  >
                    {column.sortable !== false ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={() => handleRequestSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <StyledTableRow
                  hover
                  key={row.id || index}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {columns.map((column) => {
                    const value = row[column.id];
                    return (
                      <StyledTableCell key={column.id} align={column.align || 'left'}>
                        {column.renderCell ? (
                          column.renderCell(row)
                        ) : column.format ? (
                          column.format(value, row)
                        ) : column.type === 'status' ? (
                          <Chip
                            size="small"
                            label={value}
                            color={
                              value === 'active' ? 'success' :
                              value === 'pending' ? 'warning' :
                              value === 'deleted' ? 'error' :
                              'default'
                            }
                            variant="outlined"
                          />
                        ) : (
                          value
                        )}
                      </StyledTableCell>
                    );
                  })}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default StyledDataTable;